import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle,
  Error,
  Warning,
  Info,
  Refresh,
} from '@mui/icons-material';

// Transition component
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Individual Toast Component
function Toast({ 
  id,
  message, 
  type = 'info', 
  title = null,
  action = null,
  duration = 5000,
  progress = false,
  onClose,
  persistent = false,
}) {
  const [open, setOpen] = useState(true);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway' && persistent) {
      return;
    }
    setOpen(false);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'error':
        return <Error />;
      case 'warning':
        return <Warning />;
      default:
        return <Info />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={persistent ? null : duration}
      onClose={handleClose}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ mb: id * 1 }} // Stack toasts
    >
      <Alert
        severity={type}
        variant="filled"
        onClose={handleClose}
        icon={getIcon()}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {action && (
              <IconButton
                size="small"
                color="inherit"
                onClick={action.onClick}
              >
                {action.icon || <Refresh />}
              </IconButton>
            )}
            <IconButton
              size="small"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          minWidth: 300,
          boxShadow: 6,
          '& .MuiAlert-message': {
            flex: 1,
          },
        }}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        <Typography variant="body2">
          {message}
        </Typography>
        {progress && (
          <LinearProgress
            color="inherit"
            sx={{ mt: 1, opacity: 0.8 }}
          />
        )}
      </Alert>
    </Snackbar>
  );
}

// Toast Provider Component
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [toastIdCounter, setToastIdCounter] = useState(0);

  const showToast = useCallback((options) => {
    const toastId = toastIdCounter;
    setToastIdCounter(prev => prev + 1);

    const toast = {
      id: toastId,
      ...options,
    };

    setToasts(prev => [...prev, toast]);

    // Return toast ID for programmatic control
    return toastId;
  }, [toastIdCounter]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message, options = {}) => {
    return showToast({ ...options, message, type: 'success' });
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast({ 
      ...options, 
      message, 
      type: 'error',
      duration: options.duration || 7000,
    });
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast({ ...options, message, type: 'warning' });
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast({ ...options, message, type: 'info' });
  }, [showToast]);

  const loading = useCallback((message, options = {}) => {
    return showToast({ 
      ...options, 
      message, 
      type: 'info',
      progress: true,
      persistent: true,
    });
  }, [showToast]);

  const promise = useCallback(async (
    promiseFn,
    { 
      loading: loadingMsg = 'Loading...', 
      success: successMsg = 'Success!', 
      error: errorMsg = 'Something went wrong',
    }
  ) => {
    const toastId = showToast({
      message: loadingMsg,
      type: 'info',
      progress: true,
      persistent: true,
    });

    try {
      const result = await promiseFn;
      removeToast(toastId);
      showToast({
        message: successMsg,
        type: 'success',
      });
      return result;
    } catch (error) {
      removeToast(toastId);
      showToast({
        message: errorMsg,
        type: 'error',
        action: {
          icon: <Refresh />,
          onClick: () => promise(promiseFn, { loading: loadingMsg, success: successMsg, error: errorMsg }),
        },
      });
      throw error;
    }
  }, [showToast, removeToast]);

  const value = {
    showToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    loading,
    promise,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            {...toast}
            id={index}
            onClose={removeToast}
          />
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

// Hook for async operations with toast feedback
export function useAsyncToast() {
  const toast = useToast();

  const execute = useCallback(async (
    asyncFn,
    options = {}
  ) => {
    const {
      loadingMessage = 'Processing...',
      successMessage = 'Operation completed successfully',
      errorMessage = 'Operation failed',
      showLoading = true,
    } = options;

    let loadingToastId;
    
    if (showLoading) {
      loadingToastId = toast.loading(loadingMessage);
    }

    try {
      const result = await asyncFn();
      
      if (loadingToastId !== undefined) {
        toast.removeToast(loadingToastId);
      }
      
      toast.success(successMessage);
      return result;
    } catch (error) {
      if (loadingToastId !== undefined) {
        toast.removeToast(loadingToastId);
      }
      
      const errorMsg = error.message || errorMessage;
      toast.error(errorMsg, {
        action: {
          icon: <Refresh />,
          onClick: () => execute(asyncFn, options),
        },
      });
      
      throw error;
    }
  }, [toast]);

  return { execute };
}

export default ToastProvider;